import {
  isBlankConditionValue,
  isDisplayableDate,
  splitListValue,
} from 'containers/OfferLetterTemplates/constants';

import {
  ConditionalBlock,
  ConditionField,
  ConditionOperator,
  FormField,
  OfferLetterFormData,
  VisibilityCondition,
} from './types';

/**
 * AXHE-4187 — which fields the Send Offer form is currently asking for.
 *
 * Two rules, ANDed, mirroring `OfferLetterTemplates::FieldVisibilityResolver`:
 *
 *   1. the field's own conditions pass, or it has none ("ask for Relocation
 *      Package only when Work Location says relocation"), and
 *   2. it is not confined to document sections the answers exclude — "if I'm
 *      not including the 'Re-location' section in the offer, than I don't need
 *      any of the data keys in that section either" (product, 2026-08-03).
 *
 * Rule 2 spares one shape: a field inside a section conditioned on the field's
 * own data key. That field IS the section's control, so withdrawing it locks the
 * section out for good — see `selfGatingKeys`.
 *
 * The server resolves both too, and is the authority: it decides what reaches
 * the readiness gate, the approval rules and the document. This module exists
 * because the recruiter must see a field appear and disappear as they change
 * the driving answer, and a request per keystroke is not a form.
 *
 * ## Why the composition is an AND and cannot be flattened
 *
 * Rule 1's entries are OR-combined — a field appears if ANY of its own rules
 * holds. Rule 2 is then ANDed on top. So the section rule could NOT simply be
 * appended to `visible_when`: that would read as "own condition passes OR the
 * section is in", the wrong operator, and the client would paint a form that
 * disagrees with what the server merges into a legally binding offer. Being
 * wrong is worse than the gap this replaces, where the client was merely
 * incomplete. Hence two separate keys — `visible_when` and
 * `block_occurrences` — combined explicitly, in one function, here.
 *
 * ## Why client and server can't drift
 *
 * Both resolve the same rules against the same values, and both are total
 * functions of the payload:
 *
 *   - the OPERATORS are a fixed table mirrored from
 *     `OfferLetterTemplateFieldCondition::OPERATORS`, pinned by a test that
 *     fails if the server grows one this file doesn't implement;
 *   - the VALUES come from the form's own inputs. Every data key a condition
 *     tests has a control on this form — either a mapped `FormField` carrying
 *     that `data_key`, or a `ConditionField` emitted for it — because
 *     `OfferLetters::OfferLetterForm` guarantees exactly one input per
 *     condition data key. So the client never has to reach for the applicant
 *     record the way the server's `MergeKeys::Renderer` fallback does: the
 *     server has already resolved that fallback into the control's `value`.
 *   - MEMBERSHIP (which sections enclose which variables) is a property of the
 *     template's .docx, fixed the moment the document was uploaded, so it ships
 *     once in the payload rather than being re-derived. It cannot change as the
 *     recruiter types.
 *
 * The consequence worth stating plainly: this module never decides what goes
 * into the offer. It decides what is on screen, and nothing else — the Send
 * Offer form submits every field's answer and lets
 * `FieldVisibilityResolver.visible_values` decide what is kept, which it does on
 * every write path whatever this file concluded.
 *
 * That separation is load-bearing rather than stylistic. Filtering the payload
 * here too would not add a guarantee (the server already has it, and holds it
 * against a stale tab or a direct API client); it would only feed the server a
 * different input than the one this module resolved the form from, and the two
 * would then disagree about which SECTIONS are in. See `effectiveValues` in
 * `index.tsx`.
 */

/**
 * Every operator the server can send. Exported so a test can assert this list
 * is exhaustive against `ConditionOperator` — an operator added server-side
 * without an arm here would otherwise fall through to "condition not met" and
 * silently hide a field forever.
 */
export const CONDITION_OPERATORS: ConditionOperator[] = [
  'equals',
  'not_equals',
  'present',
  'blank',
  'greater_than',
  'less_than',
  'contains',
  'in',
  'not_in',
];

// "CA, ND, OK" -> ["CA", "ND", "OK"]. Shared with the template editor rather
// than mirrored a second time — unlike the operator table above, this is a
// value-parsing rule with nothing for an exhaustiveness test to pin, so a
// private copy would just be a second place to fix.
const splitList = splitListValue;

// A calendar date, optionally followed by an ISO 8601 time and offset. Mirrors
// `FieldConditionEvaluator::ISO_DATE`: a stored applicant value can carry a
// time, and its date part is read as written, never shifted into a zone.
const ISO_DATE_TIME = /^(\d{4}-\d{2}-\d{2})(?:T[\d:.]+(?:Z|[+-]\d{2}:?\d{2})?)?$/;

// `YYYY-MM-DD`, or null for any other shape and for a day that does not exist.
const isoDay = (value: string | null): string | null => {
  const match = ISO_DATE_TIME.exec((value ?? '').trim());
  if (!match || !isDisplayableDate(match[1])) return null;
  return match[1];
};

/**
 * Mirrors `FieldConditionEvaluator#ordered_compare`: numbers first, then
 * calendar dates, false for anything else. `YYYY-MM-DD` sorts as a string in
 * date order, so two days compare as strings.
 */
const orderedCompare = (
  actual: string,
  expected: string | null,
  compare: <T extends number | string>(a: T, b: T) => boolean,
): boolean => {
  // Ruby's `Float(x, exception: false)` returns nil for a blank or non-numeric
  // string; JS's Number('') is 0, so blanks are rejected explicitly.
  if (isBlankConditionValue(actual) || isBlankConditionValue(expected)) return false;

  const a = Number(actual);
  const b = Number(expected);
  if (!Number.isNaN(a) && !Number.isNaN(b)) return compare(a, b);

  const dayA = isoDay(actual);
  const dayB = isoDay(expected);
  if (dayA == null || dayB == null) return false;
  return compare(dayA, dayB);
};

/** Mirrors `FieldConditionEvaluator#satisfied?`. */
export const conditionSatisfied = (
  condition: VisibilityCondition,
  values: Record<string, string>,
): boolean => {
  const actual = values[condition.data_key] ?? '';
  const expected = condition.value ?? '';

  switch (condition.operator) {
    case 'equals':
      return actual === expected;
    case 'not_equals':
      return actual !== expected;
    case 'present':
      return !isBlankConditionValue(actual);
    case 'blank':
      return isBlankConditionValue(actual);
    case 'greater_than':
      return orderedCompare(actual, condition.value, (a, b) => a > b);
    case 'less_than':
      return orderedCompare(actual, condition.value, (a, b) => a < b);
    case 'contains':
      return actual.includes(expected);
    case 'in':
      return splitList(condition.value).includes(actual);
    case 'not_in':
      return !splitList(condition.value).includes(actual);
    default:
      // An operator this client doesn't know — only reachable if the server
      // grows one without this table following, which CONDITION_OPERATORS'
      // exhaustiveness test exists to catch. Matches the server's own `else
      // false` deliberately: showing the input while the server treats the
      // field as not part of the offer would let a recruiter fill in a value
      // that is then silently discarded from a legally binding document.
      // Better both sides agree it isn't being asked.
      return false;
  }
};

/**
 * The data-key-keyed view of everything the recruiter has answered — the
 * client's twin of the server's condition overlay. Mapped fields contribute
 * under their `data_key`, so editing "Work Location" moves every rule keyed on
 * `work_location` without the recruiter answering it twice.
 *
 * Blank counts as "not answered", exactly as it does on the server: the form
 * echoes untouched prefills back, so an empty string can't be told apart from a
 * field nobody edited.
 */
export const resolveDataKeyValues = (
  form: OfferLetterFormData,
  values: Record<string, string>,
  conditionValues: Record<string, string>,
): Record<string, string> => {
  const resolved: Record<string, string> = {};

  // `isBlankConditionValue`, not `value !== ''` (Reef, #3353). The operator arms
  // above have applied Rails' whitespace-is-blank since AXHE-4001; this step —
  // deciding which answers make it into the overlay at all — did not, so a
  // whitespace-only answer was blank to the server and answered to the browser.
  // A prefill of "  " then revealed a field the server considered hidden, and
  // the recruiter filled in an input whose answer the API boundary discards.
  const put = (key: string | null, value: string) => {
    if (key && !isBlankConditionValue(value)) resolved[key] = value;
  };

  // The first candidate that is actually answered, by Rails' definition.
  //
  // This is the other half of the same fix, and `put` alone would have been
  // WRONG without it: the fallbacks below used `||`, i.e. JS truthiness, so a
  // whitespace-only answer is truthy and suppresses the fallback. Tightening
  // only `put` would then have dropped the key entirely rather than falling back
  // to the prefill — so a condition on it would evaluate against '' instead of
  // the applicant's real value, hiding a field the server reveals. Same class of
  // divergence, opposite direction.
  const answered = (...candidates: (string | null | undefined)[]): string =>
    candidates.find(candidate => !isBlankConditionValue(candidate)) ?? '';

  // A cleared input is "not answered", and the server likewise falls back to the
  // mapping for a blank rather than treating it as an intentional empty value
  // (MergeValueResolver#raw_value_for). `field.value` is the server's own
  // resolution of that fallback.
  form.fields.forEach((field: FormField) =>
    put(field.data_key, answered(values[field.key], field.value)),
  );
  // A condition field's own value: what the recruiter answered, else the
  // prefill the server already resolved from the applicant record. This is what
  // stands in for the server's MergeKeys::Renderer fallback, which the client
  // has no access to.
  form.condition_fields.forEach((field: ConditionField) =>
    put(field.key, answered(conditionValues[field.key], field.value)),
  );
  // Then every answered data key, whether or not the payload emitted a control
  // for it — the same wholesale merge the server's overlay does with
  // `condition_values`, and the same precedence (condition values win). Scoping
  // this to `form.condition_fields` would silently ignore an answer the client
  // is holding for a key the payload didn't enumerate, and the client would
  // then hide a field the server considers revealed.
  Object.entries(conditionValues).forEach(([key, value]) => put(key, value));

  return resolved;
};

/**
 * The document sections these answers leave OUT, by block key. Mirrors
 * `OfferLetterTemplates::ConditionalBlockResolver`, whose default is the
 * opposite of a field's: a block with NO conditions resolves to excluded,
 * because an unconfigured block is incomplete authoring rather than the normal
 * case. (A field with no conditions is always asked.)
 *
 * Re-resolved here rather than read off `block.included`, and the difference is
 * not cosmetic. `included` is the server's answer under the values the recruiter
 * had submitted at the last round trip, and — deliberately — under the VISIBLE
 * ones only, because it describes the document that will actually be rendered.
 * Visibility is decided in one pass from the values as SUBMITTED (see the
 * resolver's "one pass, deliberately"), so reusing `included` here would answer
 * a different question and could disagree with the server on which fields are
 * asked for.
 */
export const excludedBlockKeys = (
  blocks: ConditionalBlock[],
  dataKeyValues: Record<string, string>,
): Set<string> =>
  new Set(
    blocks
      .filter(
        block =>
          !block.included_when.some(condition => conditionSatisfied(condition, dataKeyValues)),
      )
      .map(block => block.key),
  );

/**
 * Rule 1 on its own: the field's own conditions, OR-combined, with "no
 * conditions" meaning always asked. Named rather than inlined so the AND below
 * reads as the two rules the server composes, and so "which rule withdrew this
 * field?" stays answerable — the server draws the same line, and only rule 1's
 * answers are dropped from `visible_values`.
 */
const ownConditionsPass = (field: FormField, dataKeyValues: Record<string, string>): boolean =>
  field.visible_when.length === 0 ||
  field.visible_when.some(condition => conditionSatisfied(condition, dataKeyValues));

/** Rule 2 on its own — see `isFieldVisible` for why it is `every`, not `some`. */
const confinedToExcludedSections = (field: FormField, excludedBlocks: Set<string>): boolean =>
  field.block_occurrences.length > 0 &&
  field.block_occurrences.every(chain => chain.some(key => excludedBlocks.has(key)));

/** Each block's own conditions, by block key. */
export const blockConditions = (
  blocks: ConditionalBlock[],
): Map<string, VisibilityCondition[]> =>
  new Map(blocks.map(block => [block.key, block.included_when]));

/**
 * The fields the section rule withdraws and nothing else can bring back —
 * mirrors `FieldVisibilityResolver#self_gating_keys`.
 *
 * `OfferLetters::OfferLetterForm` emits one input per condition data key and
 * leaves a key a mapped field covers to that field, so such a field is the
 * recruiter's only control over the key that decides whether its own section is
 * in. Withdraw it and the key keeps whatever the applicant record says, that
 * answer excludes the section, and the exclusion withdraws the field again —
 * nothing on screen breaks the loop.
 *
 * Two conditions hold the exemption to that deadlock, and it needs both.
 *
 * Read per OCCURRENCE rather than across all of them. A chain qualifies only
 * when the blocks holding it out are exactly the ones keyed on this field's own
 * data key: any OTHER excluded ancestor is a section the recruiter puts back by
 * answering ITS key, and doing so leaves this chain held out by the self-keyed
 * block alone, where the exemption then applies. Flattening the chains instead
 * would exempt a field nested three deep under a relocation section the
 * recruiter has already declined.
 *
 * And the field must be the key's only input. Nothing stops two fields sharing a
 * `data_key`, and if one of them is asked for anyway then the recruiter can
 * already answer the key, so the loop is broken without an exemption. Both being
 * withdrawn is not covered: exempting each shows one control more than the
 * deadlock needs, which is the safe direction.
 *
 * Only the FORM makes this exception. The server's readiness gate still skips
 * the field, because a variable confined to excluded sections prints nothing and
 * cannot block a send.
 */
export const selfGatingKeys = (
  fields: FormField[],
  excludedBlocks: Set<string>,
  conditionsByBlock: Map<string, VisibilityCondition[]>,
): Set<string> => {
  const withdrawn = fields.filter(field => confinedToExcludedSections(field, excludedBlocks));
  const coveredElsewhere = (field: FormField): boolean =>
    fields.some(
      other =>
        other.key !== field.key &&
        other.data_key === field.data_key &&
        !withdrawn.includes(other),
    );

  return new Set(
    withdrawn
      .filter(field => field.data_key != null && !coveredElsewhere(field))
      .filter(field => {
        const keyedOnOwn = (blockKey: string) =>
          (conditionsByBlock.get(blockKey) ?? []).some(
            condition => condition.data_key === field.data_key,
          );
        return field.block_occurrences.some(
          chain =>
            chain.some(keyedOnOwn) &&
            !chain.some(blockKey => !keyedOnOwn(blockKey) && excludedBlocks.has(blockKey)),
        );
      })
      .map(field => field.key),
  );
};

/**
 * Whether the form asks for `field` under the answers given so far — the AND of
 * the field's own rule and the section rule.
 *
 * A field is withdrawn by its sections only when EVERY occurrence of it in the
 * document sits under at least one excluded block. A variable that also renders
 * at top level (or inside a section that IS included) is still part of the
 * offer, so it is still asked for — `every`, not `some`. A field with no
 * recorded occurrences is never withdrawn: the parser didn't find it, the
 * template has no document, or there are no sections at all.
 *
 * A self-gating field is exempt from the section rule — see `selfGatingKeys`.
 *
 * `excludedBlocks` and `selfGating` are required rather than defaulted to empty.
 * A default would let a caller drop a rule by writing one fewer argument and get
 * back an older answer with nothing to notice — precisely the silent
 * half-application this whole composition exists to close.
 */
export const isFieldVisible = (
  field: FormField,
  dataKeyValues: Record<string, string>,
  excludedBlocks: Set<string>,
  selfGating: Set<string>,
): boolean =>
  ownConditionsPass(field, dataKeyValues) &&
  (selfGating.has(field.key) || !confinedToExcludedSections(field, excludedBlocks));

/**
 * The fields to render, in payload order.
 *
 * Hidden fields are removed rather than disabled. A disabled input still reads
 * as "part of this offer, just not editable right now", which is the opposite
 * of what has happened: the question does not apply, and its answer will not be
 * merged.
 */
export const visibleFields = (
  form: OfferLetterFormData,
  values: Record<string, string>,
  conditionValues: Record<string, string>,
): FormField[] => {
  const dataKeyValues = resolveDataKeyValues(form, values, conditionValues);
  const excludedBlocks = excludedBlockKeys(form.conditional_blocks, dataKeyValues);
  const conditionsByBlock = blockConditions(form.conditional_blocks);
  const selfGating = selfGatingKeys(form.fields, excludedBlocks, conditionsByBlock);
  return form.fields.filter(field =>
    isFieldVisible(field, dataKeyValues, excludedBlocks, selfGating),
  );
};
